import {
  type GetPermissionGroupsParams,
  type PermissionGroup,
  type PermissionGroupHistory,
  type PermissionGroupHistoryParams,
  type PermissionGroupMetadata,
  getPermissionGroup,
  getPermissionGroups,
  getPermissionGroupHistory,
  getPermissionGroupMetadata,
} from '@kizen/api/permission-group';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Drop, Resource, WithParams, WithRequired } from './types';
import { curry1, getResource } from './utils';

type PermissionGroupQuery<
  TData = PermissionGroup,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    PermissionGroup,
    TError,
    TData,
    PermissionGroup,
    [Resource<Readonly<['permission-group', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

type PermissionGroupsQuery<
  TData = PermissionGroup[],
  TError = DefaultError,
> = WithParams<
  QueryObserverOptions<
    PermissionGroup[],
    TError,
    TData,
    PermissionGroup[],
    [Resource<Readonly<['permission-group']>, GetPermissionGroupsParams>]
  >,
  'queryFn' | 'queryKey'
>;

type PermissionGroupHistoryQuery<
  TData = PermissionGroupHistory,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    PermissionGroupHistory,
    TError,
    TData,
    PermissionGroupHistory,
    [
      Resource<
        Readonly<['permission-group', string, 'history']>,
        PermissionGroupHistoryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type PermissionGroupMetadataQuery<
  TData = PermissionGroupMetadata,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    PermissionGroupMetadata,
    TError,
    TData,
    PermissionGroupMetadata,
    [Resource<Readonly<['permissions', 'meta-data']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const permissionGroupQuery = <TData = PermissionGroup>(
  axios: AxiosInstance,
  id: string,
  options: Omit<PermissionGroupQuery<TData>, 'queryFn' | 'queryKey'> = {}
): PermissionGroupQuery<TData> => {
  const path = ['permission-group', id] as const;
  const resource = getResource(path);

  return <PermissionGroupQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getPermissionGroup(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const permissionGroupsQuery = <TData = PermissionGroup[]>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<PermissionGroupsQuery<TData>, 'queryFn' | 'queryKey'>,
    GetPermissionGroupsParams
  > = {}
): PermissionGroupsQuery<TData> => {
  const path = ['permission-group'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return <PermissionGroupsQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getPermissionGroups(axios, { params: qk.params, signal });
    },
    ...queryOptions,
  };
};

export const permissionGroupHistoryQuery = <TData = PermissionGroupHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<PermissionGroupHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    PermissionGroupHistoryParams
  > = {}
): PermissionGroupHistoryQuery<TData> => {
  const path = ['permission-group', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return <PermissionGroupHistoryQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getPermissionGroupHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const permissionGroupMetadataQuery = <TData = PermissionGroupMetadata>(
  axios: AxiosInstance,
  options: Omit<PermissionGroupMetadataQuery, 'queryFn' | 'queryKey'> = {}
): PermissionGroupMetadataQuery<TData> => {
  const path = ['permissions', 'meta-data'] as const;
  const resource = getResource(path);

  return <PermissionGroupMetadataQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;

      return getPermissionGroupMetadata(axios, { signal });
    },
    ...options,
  };
};

class Queries {
  public permissionGroupQuery: <TData = PermissionGroup>(
    ...args: Drop<Parameters<typeof permissionGroupQuery<TData>>>
  ) => PermissionGroupQuery<TData>;
  public permissionGroupsQuery: <TData = PermissionGroup[]>(
    ...args: Drop<Parameters<typeof permissionGroupsQuery<TData>>>
  ) => PermissionGroupsQuery<TData>;
  public permissionGroupHistoryQuery: <TData = PermissionGroupHistory>(
    ...args: Drop<Parameters<typeof permissionGroupHistoryQuery<TData>>>
  ) => PermissionGroupHistoryQuery<TData>;
  public permissionGroupMetadataQuery: <TData = PermissionGroupMetadata>(
    ...args: Drop<Parameters<typeof permissionGroupMetadataQuery<TData>>>
  ) => PermissionGroupMetadataQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.permissionGroupQuery = curry1(permissionGroupQuery)(axios);
    this.permissionGroupsQuery = curry1(permissionGroupsQuery)(axios);
    this.permissionGroupHistoryQuery = curry1(permissionGroupHistoryQuery)(
      axios
    );
    this.permissionGroupMetadataQuery = curry1(permissionGroupMetadataQuery)(
      axios
    );
  }
}

export type PermissionGroupQueries = Queries;

export const defPermissionGroupQueries = (
  axios: AxiosInstance
): PermissionGroupQueries => {
  return new Queries(axios);
};
